// src/components/materials/MaterialsPanel.tsx
//
// PitchLess materials, on a CRM record.
//
// ============================================================================
// ONE PANEL, FOUR SURFACES
// ============================================================================
//
// Estimate, Opportunity, Company and Documents all show the same thing: the
// materials that came from a record, what state each is in, and the actions
// PitchLess says this user may take. They differ only in WHICH record, so
// this is one component parameterised by `entityType`/`entityId` rather than
// four near-copies that drift apart the first time review gains a state.
//
// ============================================================================
// WHAT THIS COMPONENT REFUSES TO DECIDE
// ============================================================================
//
// It never computes a permission. Whether the current user may refine,
// submit or approve comes from `myRole` on the server's response, because
// PitchLess applies app roles and per-artifact collaboration rules this
// client cannot see. Deriving it here from `ownerUserId` would be wrong in
// exactly the cases that matter - a reviewer who is not the owner.
//
// It also never reconstructs state after a mutation. Refining does not patch
// a local object; it invalidates and re-reads, because only PitchLess knows
// the new version id, whether review advanced, and whether the workspace was
// charged.
//
// ============================================================================
// THE PREVIEW IS UNTRUSTED CONTENT
// ============================================================================
//
// It is HTML a generator wrote. It renders in an iframe with an EMPTY sandbox
// and a blob: URL - no `allow-scripts`, no `allow-same-origin`, so it lives in
// an opaque origin with scripting off. Those two flags must never both appear:
// together they let a frame remove its own sandbox. See useMaterialFile.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useGetMaterialsConfigQuery,
  useGetMaterialTypesQuery,
  normaliseMaterialTypes,
  materialTypeLabel,
  type MaterialType,
  useGetMaterialsForEntityQuery,
  useRefineMaterialMutation,
  useSubmitMaterialForReviewMutation,
  useDecideMaterialReviewMutation,
  materialErrorCode,
  isBusy,
  hasContent,
  type Material,
  type MaterialSurface,
} from '../../api/materialsApi';
import { MaterialStatusBadge } from './MaterialStatusBadge';
import { CreateMaterialDialog } from './CreateMaterialDialog';
import { useMaterialFile } from './useMaterialFile';

export interface MaterialsPanelProps {
  entityType: MaterialSurface;
  entityId: string;
  /** What the user calls this record. Shown in the create dialog. */
  entityLabel: string;
  /** Rendered without its own heading when embedded in a section that has one. */
  headingLevel?: 'h2' | 'h3';
}

/** Generation is asynchronous; poll only while something is actually moving. */
const BUSY_POLL_MS = 4000;

export function MaterialsPanel({
  entityType,
  entityId,
  entityLabel,
  headingLevel = 'h3',
}: MaterialsPanelProps): React.ReactElement | null {
  const { data: config } = useGetMaterialsConfigQuery();
  // The canonical registry, for turning `artifactTypeKey` into words. Same RTK
  // cache entry the create dialog uses (`providesTags: TYPES`), so opening a
  // panel costs no extra request once either surface has loaded it.
  //
  // Not an optional nicety: this row rendered the raw key — `sales_presentation`
  // — to the user. The create dropdown had the identical defect and is fixed in
  // the same pass; leaving one of the two would keep a surface that disagrees
  // with the other about what a material is called.
  const { data: typesData } = useGetMaterialTypesQuery();
  const types = useMemo(() => normaliseMaterialTypes(typesData), [typesData]);
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Poll ONLY while something is generating, and stop the moment it settles.
  // A permanent interval on every estimate row would be steady load on both
  // apps for a panel that is static almost all of the time. `pollingInterval:
  // 0` is RTK Query's "do not poll", so this is one subscription whose cadence
  // changes, not a second query.
  const [busy, setBusy] = useState(false);
  const { data, isLoading, isError, error, refetch } = useGetMaterialsForEntityQuery(
    { entityType, entityId },
    { skip: !config?.configured, pollingInterval: busy ? BUSY_POLL_MS : 0 }
  );

  const materials = useMemo(() => data?.artifacts ?? [], [data]);
  const anyBusy = materials.some((m) => isBusy(m.status));
  useEffect(() => {
    setBusy(anyBusy);
  }, [anyBusy]);

  // The integration is not live here. Render NOTHING rather than a button
  // that can only fail - an unconfigured environment is a setup gap, not a
  // feature the user should be invited to try.
  if (config && !config.configured) return null;

  const Heading = headingLevel;
  const existingTypeKeys = materials.map((m) => m.artifactTypeKey);

  return (
    <section
      data-testid="materials-panel"
      className="w-full max-w-full overflow-x-hidden border border-gray-200 rounded-lg bg-white"
      aria-labelledby="materials-heading"
    >
      <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <Heading id="materials-heading" className="text-sm font-semibold text-gray-900">
            Materials
          </Heading>
          <p className="text-xs text-gray-500">
            Proposals and collateral generated in PitchLess
          </p>
        </div>
        <button
          type="button"
          data-testid="materials-create-button"
          onClick={() => setCreating(true)}
          className="px-3 py-2 min-h-[44px] text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
        >
          Create material
        </button>
      </div>

      <div className="divide-y divide-gray-100">
        {isLoading && <p className="px-4 py-6 text-sm text-gray-500">Loading materials...</p>}

        {isError && (
          <div className="px-4 py-6">
            <p className="text-sm text-red-600">
              {materialErrorCode(error) === 'not_configured'
                ? 'PitchLess is not connected in this environment.'
                : 'Could not load materials from PitchLess.'}
            </p>
            {materialErrorCode(error) !== 'not_configured' && (
              <button
                type="button"
                onClick={() => void refetch()}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700"
              >
                Try again
              </button>
            )}
          </div>
        )}

        {!isLoading && !isError && materials.length === 0 && (
          <p className="px-4 py-6 text-sm text-gray-500">
            No materials yet. Create one to generate a proposal or one-pager from this record.
          </p>
        )}

        {materials.map((m) => (
          <MaterialRow
            types={types}
            key={m.id}
            material={m}
            expanded={expandedId === m.id}
            onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)}
            appUrl={config?.appUrl ?? null}
          />
        ))}
      </div>

      {creating && (
        <CreateMaterialDialog
          entityType={entityType}
          entityId={entityId}
          entityLabel={entityLabel}
          existingTypeKeys={existingTypeKeys}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            if (id) setExpandedId(id);
          }}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// One material
// ---------------------------------------------------------------------------

interface MaterialRowProps {
  types: readonly MaterialType[];
  material: Material;
  expanded: boolean;
  onToggle: () => void;
  appUrl: string | null;
}

function MaterialRow({ material, types, expanded, onToggle, appUrl }: MaterialRowProps): React.ReactElement {
  const m = material;
  const ready = hasContent(m);

  return (
    <div className="px-4 py-3" data-testid="material-row">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          data-testid="material-row-toggle"
          className="flex-1 min-w-0 text-left py-2 min-h-[44px]"
        >
          <p className="font-medium text-gray-900 truncate">{m.title || 'Untitled material'}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <MaterialStatusBadge status={m.status} reviewStatus={m.reviewStatus} />
            <span className="text-xs text-gray-500">
              {materialTypeLabel(types, m.artifactTypeKey)}
            </span>
          </div>
        </button>

        {appUrl && (
          <a
            href={`${appUrl}/artifacts/${encodeURIComponent(m.id)}`}
            target="_blank"
            // `noopener` is not decoration: without it the opened tab gets a
            // handle on this one via window.opener.
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-700 whitespace-nowrap"
          >
            Open in PitchLess
          </a>
        )}
      </div>

      {m.reviewStatus === 'needs-revision' && m.reviewerNote && (
        <p className="mt-2 text-sm text-orange-800 bg-orange-50 border border-orange-200 rounded p-2">
          <span className="font-medium">Changes requested:</span> {m.reviewerNote}
        </p>
      )}

      {expanded && <MaterialDetail material={m} ready={ready} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview, refine, review, export
// ---------------------------------------------------------------------------

function MaterialDetail({
  material,
  ready,
}: {
  material: Material;
  ready: boolean;
}): React.ReactElement {
  const m = material;
  const [refine, { isLoading: refining }] = useRefineMaterialMutation();
  const [submit, { isLoading: submitting }] = useSubmitMaterialForReviewMutation();
  const [decide, { isLoading: deciding }] = useDecideMaterialReviewMutation();
  const { fetchFile, download, pending: filePending, error: fileError } = useMaterialFile();

  const [instructions, setInstructions] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  // PitchLess's answer to "what may this user do", never computed here.
  const role = m.myRole;
  const canEdit = role === 'owner' || role === 'editor';
  const canReview = role === 'owner' || role === 'reviewer';

  // PROMPT REFINEMENT IS SLIDES-ONLY UPSTREAM.
  //
  // `/v1/artifacts/:id/refine` runs the SLIDE refinement service, which
  // refuses anything else with NOT_A_SLIDES_ARTIFACT. A document-family
  // material — which is what an estimate proposal or one-pager is — can
  // never be refined through it.
  //
  // So the control is not offered for documents. Showing a Revise box that
  // always fails is worse than not showing one: it teaches the user the
  // feature is broken rather than that it is elsewhere. They are pointed at
  // PitchLess instead, where the document editor lives.
  const canRefineHere = canEdit && m.family === 'slides';

  const loadPreview = useCallback(async () => {
    const file = await fetchFile(m.id, 'html', m.currentVersionId ?? undefined);
    if (file) setPreviewUrl(file.objectUrl);
  }, [fetchFile, m.id, m.currentVersionId]);

  const run = async (fn: () => Promise<unknown>, conflictMessage: string): Promise<void> => {
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      const code = materialErrorCode(e);
      setActionError(
        code === 'conflict'
          ? conflictMessage
          : code === 'forbidden'
            ? 'PitchLess did not allow that. Your access may have changed.'
            : code === 'not_configured'
              ? 'PitchLess is not connected in this environment.'
              : 'That did not go through. Please try again.'
      );
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-4">
      {/* ── Preview ─────────────────────────────────────────────── */}
      <div>
        {!ready && (
          <p className="text-sm text-gray-500">
            {isBusy(m.status)
              ? 'PitchLess is still generating this. It will appear here when ready.'
              : 'This material has no content yet.'}
          </p>
        )}

        {ready && !previewUrl && (
          <button
            type="button"
            onClick={() => void loadPreview()}
            data-testid="material-preview-button"
            disabled={filePending}
            className="px-3 py-2 min-h-[44px] text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {filePending ? 'Loading preview...' : 'Show preview'}
          </button>
        )}

        {previewUrl && (
          <iframe
            // EMPTY sandbox + blob: URL. No allow-scripts, no
            // allow-same-origin — together those would let the frame remove
            // its own sandbox. This is generated content, not CRM's.
            sandbox=""
            data-testid="material-preview-frame"
            src={previewUrl}
            title={`Preview of ${m.title || 'material'}`}
            className="w-full h-80 sm:h-96 border border-gray-200 rounded bg-white"
          />
        )}

        {fileError && (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {fileError}
          </p>
        )}
      </div>

      {/* ── Export ──────────────────────────────────────────────── */}
      {ready && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-600">Export:</span>
          {(['pdf', 'docx', 'html'] as const).map((fmt) => (
            <button
              key={fmt}
              type="button"
              disabled={filePending}
              onClick={() => void download(m.id, fmt, m.currentVersionId ?? undefined)}
              className="px-3 py-2 min-h-[44px] text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 uppercase"
            >
              {fmt}
            </button>
          ))}
        </div>
      )}

      {/* ── Refine ──────────────────────────────────────────────── */}
      {canEdit && ready && m.family !== 'slides' && (
        <p className="text-sm text-gray-500">
          Revising a document happens in PitchLess, where the editor lives.
        </p>
      )}

      {canRefineHere && ready && (
        <div>
          <label
            htmlFor={`refine-${m.id}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Ask for a change
          </label>
          <textarea
            id={`refine-${m.id}`}
            data-testid="material-refine-input"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={2}
            placeholder="e.g. Make the pricing section shorter and lead with the outcome"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={instructions.trim() === '' || refining}
              data-testid="material-refine-submit"
              onClick={() =>
                void run(async () => {
                  await refine({
                    id: m.id,
                    instructions: instructions.trim(),
                    // Optimistic-concurrency token. PitchLess answers 409 if
                    // someone else changed it first; CRM does not own the
                    // version and must not pick a winner.
                    expectedVersionId: m.currentVersionId ?? undefined,
                  }).unwrap();
                  setInstructions('');
                  setPreviewUrl(null);
                }, 'Someone else changed this material first. Reopen it to see the latest version.')
              }
              className="px-3 py-2 min-h-[44px] text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
            >
              {refining ? 'Revising...' : 'Revise'}
            </button>
            <span className="text-xs text-gray-500">Uses credits</span>
          </div>
        </div>
      )}

      {/* ── Review ──────────────────────────────────────────────── */}
      {ready && (m.reviewStatus === 'drafting' || m.reviewStatus === 'needs-revision') && canEdit && (
        <button
          type="button"
          disabled={submitting}
          data-testid="material-submit-review"
          onClick={() => void run(() => submit({ id: m.id }).unwrap(), 'Review state changed. Reopen this material.')}
          className="px-3 py-2 min-h-[44px] text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {submitting ? 'Submitting...' : 'Submit for review'}
        </button>
      )}

      {m.reviewStatus === 'in-review' && canReview && (
        <div className="rounded-lg border border-gray-200 p-3">
          <label htmlFor={`note-${m.id}`} className="block text-sm font-medium text-gray-700 mb-1">
            Review note <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            id={`note-${m.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={deciding}
              data-testid="material-approve"
              onClick={() =>
                void run(
                  () =>
                    decide({
                      id: m.id,
                      decision: 'approve',
                      note: note.trim() || undefined,
                    }).unwrap(),
                  'Review state changed. Reopen this material.'
                )
              }
              className="px-3 py-2 min-h-[44px] text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50"
            >
              Approve
            </button>
            <button
              type="button"
              disabled={deciding}
              data-testid="material-request-changes"
              onClick={() =>
                void run(
                  () =>
                    decide({
                      id: m.id,
                      decision: 'request-revision',
                      note: note.trim() || undefined,
                    }).unwrap(),
                  'Review state changed. Reopen this material.'
                )
              }
              className="px-3 py-2 min-h-[44px] text-sm font-medium text-orange-700 border border-orange-300 hover:bg-orange-50 rounded-lg disabled:opacity-50"
            >
              Request changes
            </button>
          </div>
        </div>
      )}

      {m.reviewStatus === 'in-review' && !canReview && (
        <p className="text-sm text-gray-500">
          Waiting on a reviewer in PitchLess.
        </p>
      )}

      {actionError && (
        <p className="text-sm text-red-600" role="alert">
          {actionError}
        </p>
      )}
    </div>
  );
}
